import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { inr, ymd } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, TrendingUp, Lightbulb, Info, ArrowRight, Check, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const monthStr = (d) => ymd(d);
const firstOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth(), 1);
const lastOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
const DOT = { CRITICAL: "bg-rose-500", HIGH: "bg-orange-500", MEDIUM: "bg-amber-500", LOW: "bg-slate-400" };
const BADGE = { CRITICAL: "bg-rose-100 text-rose-700", HIGH: "bg-orange-100 text-orange-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-slate-100 text-slate-600" };
const CAT_ICON = { ATTENTION: AlertTriangle, OPPORTUNITY: Lightbulb, POSITIVE: TrendingUp, INFORMATION: Info };
const SECTIONS = [["ATTENTION", "Needs Attention"], ["OPPORTUNITY", "Opportunities"], ["POSITIVE", "Positive"], ["INFORMATION", "Information"]];
const STATUS_BADGE = { OPEN: "", ACKNOWLEDGED: "bg-sky-100 text-sky-700", RESOLVED: "bg-emerald-100 text-emerald-700", DISMISSED: "bg-slate-100 text-slate-500" };
const PRANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

export default function BusinessActions() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const now = new Date();
  const [range, setRange] = useState({ mode: "current", start: monthStr(firstOfMonth(now)), end: monthStr(lastOfMonth(now)) });
  const [f, setF] = useState({ priority: "all", category: "all", service: "all", status: "all" });
  const [sort, setSort] = useState("priority");
  const [detail, setDetail] = useState(null);

  const setPreset = (mode) => {
    if (mode === "current") setRange({ mode, start: monthStr(firstOfMonth(now)), end: monthStr(lastOfMonth(now)) });
    else if (mode === "previous") { const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1); setRange({ mode, start: monthStr(firstOfMonth(pm)), end: monthStr(lastOfMonth(pm)) }); }
    else setRange((r) => ({ ...r, mode }));
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["business-actions", range.start, range.end],
    queryFn: async () => (await api.get(`/business-actions?period_start=${range.start}&period_end=${range.end}&include_closed=true`)).data,
  });

  const transition = useMutation({
    mutationFn: ({ id, t }) => api.post(`/business-actions/${id}/${t}`),
    onSuccess: (_r, v) => { qc.invalidateQueries({ queryKey: ["business-actions"] }); qc.invalidateQueries({ queryKey: ["business-actions-card"] }); setDetail(null); toast.success(`Action ${v.t}d`); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const actions = data?.actions || [];
  const services = useMemo(() => Array.from(new Set(actions.map((a) => a.related_service).filter(Boolean))), [actions]);
  const filtered = useMemo(() => {
    let out = actions.filter((a) =>
      (f.priority === "all" || a.priority === f.priority) &&
      (f.category === "all" || a.category === f.category) &&
      (f.service === "all" || a.related_service === f.service) &&
      (f.status === "all" || a.status === f.status));
    if (sort === "impact") out = [...out].sort((a, b) => (b.financial_impact?.amount || 0) - (a.financial_impact?.amount || 0));
    else if (sort === "newest") out = [...out].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else out = [...out].sort((a, b) => (PRANK[a.priority] ?? 9) - (PRANK[b.priority] ?? 9));
    return out;
  }, [actions, f, sort]);

  const Sel = ({ k, label, options }) => (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <Select value={f[k]} onValueChange={(v) => setF((p) => ({ ...p, [k]: v }))}>
        <SelectTrigger data-testid={`ba-filter-${k}`} className="h-9 bg-white min-w-[120px]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white max-h-64"><SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Business Actions" subtitle="What needs your attention, why, how much it matters, and what to review next">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant={range.mode === "current" ? "default" : "outline"} onClick={() => setPreset("current")} data-testid="ba-preset-current">This Month</Button>
          <Button size="sm" variant={range.mode === "previous" ? "default" : "outline"} onClick={() => setPreset("previous")} data-testid="ba-preset-previous">Last Month</Button>
          <Button size="sm" variant={range.mode === "custom" ? "default" : "outline"} onClick={() => setPreset("custom")} data-testid="ba-preset-custom">Custom</Button>
        </div>
      </PageHeader>

      {range.mode === "custom" && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="ba-custom-range">
          <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">From</div><Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="ba-custom-from" className="h-9 bg-white" /></div>
          <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">To</div><Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="ba-custom-to" className="h-9 bg-white" /></div>
        </div>
      )}

      {isLoading ? <Loading /> : isError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700" data-testid="ba-error">
          {error?.response?.status === 403 ? "Business Actions requires Profit Intelligence and financial access." : "Unable to load business actions."}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="ba-filters">
            <Sel k="priority" label="Priority" options={["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((v) => ({ value: v, label: v }))} />
            <Sel k="category" label="Category" options={SECTIONS.map(([v, l]) => ({ value: v, label: l }))} />
            <Sel k="service" label="Service" options={services.map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))} />
            <Sel k="status" label="Status" options={["OPEN", "ACKNOWLEDGED", "RESOLVED", "DISMISSED"].map((v) => ({ value: v, label: v }))} />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Sort</div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="ba-sort" className="h-9 bg-white min-w-[120px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="priority">Priority</SelectItem><SelectItem value="impact">Financial impact</SelectItem><SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="ba-empty">
              <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
              <p className="mt-2 font-medium text-slate-700">Nothing needs your attention right now</p>
              <p className="text-sm text-slate-500">Pawkt will surface actions here as your business data changes.</p>
            </div>
          ) : SECTIONS.map(([cat, label]) => {
            const rows = filtered.filter((a) => a.category === cat);
            if (!rows.length) return null;
            const Icon = CAT_ICON[cat];
            return (
              <div key={cat} data-testid={`ba-section-${cat}`}>
                <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-500"><Icon className="h-4 w-4" /> {label} <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{rows.length}</span></h3>
                <div className="space-y-2">
                  {rows.map((a) => (
                    <button key={a.id} data-testid={`ba-item-${a.id}`} onClick={() => setDetail(a)}
                      className="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left hover:border-slate-300 hover:shadow-sm transition">
                      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${DOT[a.priority]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-800">{a.title}</span>
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${BADGE[a.priority]}`}>{a.priority}</span>
                          {a.status !== "OPEN" && <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_BADGE[a.status]}`}>{a.status}</span>}
                        </div>
                        <p className="mt-0.5 text-sm text-slate-500 line-clamp-2">{a.summary}</p>
                      </div>
                      {a.financial_impact?.available && <div className="shrink-0 text-right"><div className="text-sm font-bold text-slate-900">{inr(a.financial_impact.amount)}</div><div className="text-[10px] text-slate-400">{a.financial_impact.kind}</div></div>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="ba-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${DOT[detail.priority]}`} />
                  <DialogTitle className="text-lg">{detail.title}</DialogTitle>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${BADGE[detail.priority]}`}>{detail.priority}</span>
                  <span className="text-xs text-slate-400">{detail.category} · {detail.period?.label || detail.period?.month}</span>
                </div>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Why Pawkt flagged this</div>
                  <p className="mt-1 text-slate-700">{detail.summary}</p>
                </div>
                {detail.evidence?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Evidence</div>
                    <div className="mt-1 divide-y divide-slate-100 rounded-lg border border-slate-100" data-testid="ba-detail-evidence">
                      {detail.evidence.map((e, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5">
                          <span className="text-slate-500">{e.label} <span className="text-[10px] text-slate-300">{e.kind}</span></span>
                          <span className="font-semibold text-slate-800">{e.money ? inr(e.value) : `${e.value}${typeof e.value === "number" && !e.money ? "" : ""}`}{!e.money && typeof e.value === "number" ? "" : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {detail.financial_impact?.available && (
                  <div className="rounded-lg bg-slate-50 p-3" data-testid="ba-detail-impact">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{detail.financial_impact.label}</div>
                    <div className="font-display text-xl font-bold text-slate-900">{inr(detail.financial_impact.amount)} <span className="text-xs font-medium text-slate-400">{detail.financial_impact.kind}</span></div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Recommended action</div>
                  <p className="mt-1 text-slate-700">{detail.recommended_action}</p>
                </div>
                {detail.inactive && <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">This action is no longer active — current data no longer meets the threshold.</div>}
                <div className="flex flex-wrap gap-2 pt-1">
                  {(detail.navigation || []).map(([label, to]) => (
                    <Button key={to} size="sm" variant="outline" className="gap-1" data-testid={`ba-nav-${to.replace(/\//g, "")}`} onClick={() => navigate(to)}>{label} <ArrowRight className="h-3.5 w-3.5" /></Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  {detail.status === "OPEN" && <Button size="sm" variant="outline" className="gap-1" data-testid="ba-acknowledge" onClick={() => transition.mutate({ id: detail.id, t: "acknowledge" })}><Check className="h-3.5 w-3.5" /> Acknowledge</Button>}
                  {detail.status !== "RESOLVED" && detail.status !== "DISMISSED" && <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ba-resolve" onClick={() => transition.mutate({ id: detail.id, t: "resolve" })}><CheckCircle2 className="h-3.5 w-3.5" /> Mark reviewed</Button>}
                  {detail.status !== "RESOLVED" && detail.status !== "DISMISSED" && <Button size="sm" variant="ghost" className="gap-1 text-slate-500" data-testid="ba-dismiss" onClick={() => transition.mutate({ id: detail.id, t: "dismiss" })}><X className="h-3.5 w-3.5" /> Dismiss</Button>}
                </div>
                <p className="text-[11px] text-slate-400">Resolving or dismissing records that you reviewed this intelligence. It does not change any financial record.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
